import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { StarInput, RATING_KEYS, RATING_LABELS } from "../components/Ratings";
import { UploadCloud, ShieldCheck, CheckCircle2, MapPin, Camera } from "lucide-react";

export default function WriteReview() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const slug = params.get("slug");

  const [addr, setAddr] = useState({ address_line: "", city: "", postcode: "" });
  const [pcSuggestions, setPcSuggestions] = useState([]);
  const [pcLoading, setPcLoading] = useState(false);
  const [ratings, setRatings] = useState({ damp_mould: 0, repair_speed: 0, energy_efficiency: 0, communication: 0, location: 0, value_for_money: 0 });
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [responsible, setResponsible] = useState("landlord");
  const [respEmail, setRespEmail] = useState("");
  const [file, setFile] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [agree, setAgree] = useState(false);
  const [done, setDone] = useState(false);
  const [step, setStep] = useState(1);
  const [unit, setUnit] = useState("");
  const [filePreview, setFilePreview] = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/register", { state: { from: slug ? `/write-review?slug=${slug}` : "/write-review" } });
  }, [user, loading, navigate]);

  // Restore a saved draft (kept 5 days) so tenants can resume where they left off
  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "tenant") { setDraftLoaded(true); return; }
    api.get("/reviews/draft").then((r) => {
      const d = r.data?.draft;
      if (d) {
        if (!slug && d.addr) setAddr(d.addr);
        if (d.unit) setUnit(d.unit);
        if (d.ratings) setRatings(d.ratings);
        if (d.title) setTitle(d.title);
        if (d.body) setBody(d.body);
        if (d.start) setStart(d.start);
        if (d.end) setEnd(d.end);
        if (d.responsible) setResponsible(d.responsible);
        if (d.respEmail) setRespEmail(d.respEmail);
      }
    }).catch(() => {}).finally(() => setDraftLoaded(true));
  }, [user, loading]); // eslint-disable-line

  // Auto-save the draft (debounced) so an abandoned review can be resumed / nudged
  useEffect(() => {
    if (!draftLoaded || done || !user || user.role !== "tenant") return;
    const hasContent = title.trim() || body.trim() || Object.values(ratings).some((v) => v > 0) || addr.address_line.trim();
    if (!hasContent) return;
    const t = setTimeout(() => {
      api.put("/reviews/draft", { data: { addr, unit, ratings, title, body, start, end, responsible, respEmail } }).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [addr, unit, ratings, title, body, start, end, responsible, respEmail, draftLoaded, done, user]);

  useEffect(() => {
    if (slug) api.get(`/properties/${slug}`).then((r) => {
      setAddr({ address_line: r.data.address_line, city: r.data.city, postcode: r.data.postcode });
    }).catch(() => {});
  }, [slug]);

  // Postcode autocomplete via free postcodes.io
  const onPostcode = async (val) => {
    setAddr((a) => ({ ...a, postcode: val }));
    const clean = val.replace(/\s/g, "");
    if (clean.length < 2) { setPcSuggestions([]); return; }
    setPcLoading(true);
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(clean)}/autocomplete`);
      const j = await res.json();
      setPcSuggestions(j.result || []);
    } catch { setPcSuggestions([]); }
    finally { setPcLoading(false); }
  };

  const selectPostcode = async (pc) => {
    setAddr((a) => ({ ...a, postcode: pc }));
    setPcSuggestions([]);
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc.replace(/\s/g, ""))}`);
      const j = await res.json();
      // Prefer the parish (usually the actual town/village, e.g. "Lancing") over the
      // local-authority district (e.g. "Adur"); fall back to the district if unparished.
      const town = j.result?.parish || j.result?.admin_district;
      if (town) setAddr((a) => ({ ...a, city: town }));
    } catch { /* ignore */ }
  };

  const onPhoto = (f) => {
    setPhoto(f);
    if (f) setPhotoPreview(URL.createObjectURL(f));
  };

  const onFile = (f) => {
    setFile(f);
    setFilePreview(f && f.type && f.type.startsWith("image/") ? URL.createObjectURL(f) : null);
  };

  const goNext = () => {
    setError("");
    if (step === 1) {
      if (!addr.postcode || !addr.address_line || !addr.city) { setError("Please complete the property address (postcode, street and town)."); return; }
    }
    if (step === 2) {
      if (Object.values(ratings).some((v) => v < 1)) { setError("Please rate all categories before continuing."); return; }
      if (!body.trim()) { setError("Please describe your experience before continuing."); return; }
    }
    setStep((s) => Math.min(3, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => { setError(""); setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (Object.values(ratings).some((v) => v < 1)) { setError("Please rate all categories."); return; }
    if (!file) { setError("Please upload your tenancy agreement."); return; }
    if (!agree) { setError("Please confirm your review is truthful and accept the Terms & Conditions."); return; }
    setSubmitting(true);
    const composedAddress = unit.trim() && !addr.address_line.toLowerCase().includes(unit.trim().toLowerCase())
      ? `${unit.trim()}, ${addr.address_line}` : addr.address_line;
    const fd = new FormData();
    fd.append("address_line", composedAddress);
    fd.append("city", addr.city);
    fd.append("postcode", addr.postcode);
    RATING_KEYS.forEach((k) => fd.append(k, ratings[k]));
    fd.append("title", title);
    fd.append("body", body);
    fd.append("tenancy_start", start);
    fd.append("tenancy_end", end);
    fd.append("responsible_party", responsible);
    fd.append("responsible_email", respEmail);
    fd.append("agreement", file);
    if (photo) fd.append("property_photo", photo);
    try {
      await api.post("/reviews", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSubmitting(false); }
  };

  if (user && user.role !== "tenant") {
    return (
      <div className="App bg-[#F4F7FA] min-h-screen">
        <Navbar />
        <div className="max-w-lg mx-auto px-5 py-24 text-center" data-testid="review-wrong-role">
          <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center mx-auto"><ShieldCheck className="w-6 h-6" /></div>
          <h1 className="mt-5 font-heading font-extrabold text-2xl text-navy">Reviews can only be left by tenants</h1>
          <p className="mt-3 text-slate-600">You're signed in with a <span className="font-semibold">{user.role === "landlord" ? "landlord" : "staff/admin"}</span> account, which can't post reviews. To leave a review, please register a separate <span className="font-semibold">tenant</span> account.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button data-testid="review-register-tenant" onClick={() => navigate("/register", { state: { from: slug ? `/write-review?slug=${slug}` : "/write-review" } })}
              className="bg-mint text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-mint-dark transition-colors">Register a tenant account</button>
            <button onClick={() => navigate("/")} className="border border-slate-300 text-slate-700 font-semibold px-5 py-2.5 rounded-xl hover:bg-slate-50 transition-colors">Back to home</button>
          </div>
        </div>
      </div>
    );
  }

  if (done) return (
    <div className="App bg-[#F4F7FA] min-h-screen"><Navbar />
      <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="review-success">
        <CheckCircle2 className="w-16 h-16 text-mint mx-auto" />
        <h1 className="mt-4 font-heading font-extrabold text-3xl text-navy">Review submitted</h1>
        <p className="mt-3 text-slate-600">Our team will verify your tenancy agreement before it goes live. Your document is deleted the moment verification completes.</p>
        <button data-testid="go-tenant-dashboard" onClick={() => navigate("/tenant")}
          className="mt-6 bg-mint text-white font-semibold px-6 py-3 rounded-xl hover:bg-mint-dark transition-colors">Go to my reviews</button>
      </div>
    </div>
  );

  return (
    <div className="App bg-[#F4F7FA] min-h-screen">
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 py-12">
        <h1 className="font-heading font-extrabold text-4xl text-navy">Write a verified review</h1>
        <p className="mt-2 text-slate-600">Rate your rental honestly. We verify every review against a real tenancy agreement.</p>
        <p className="mt-1 text-xs text-slate-400 flex items-center gap-1.5" data-testid="draft-autosave-note"><CheckCircle2 className="w-3.5 h-3.5 text-mint-dark" /> Your progress is saved automatically — come back within 5 days to finish.</p>

        {/* Step progress */}
        <div className="mt-6 flex items-center gap-2" data-testid="review-steps">
          {[{ n: 1, t: "Property" }, { n: 2, t: "Your review" }, { n: 3, t: "Proof & submit" }].map((s, i) => (
            <div key={s.n} className="flex items-center gap-2 flex-1">
              <div className={`flex items-center gap-2 ${step >= s.n ? "" : "opacity-40"}`}>
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${step > s.n ? "bg-mint text-white" : step === s.n ? "bg-navy text-white" : "bg-slate-200 text-slate-500"}`}>
                  {step > s.n ? "✓" : s.n}
                </span>
                <span className="text-sm font-semibold text-navy hidden sm:inline">{s.t}</span>
              </div>
              {i < 2 && <div className={`flex-1 h-0.5 rounded ${step > s.n ? "bg-mint" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8 flex flex-col gap-8" data-testid="review-form">
          {/* STEP 1 — Address */}
          {step === 1 && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="review-step-1">
            <div className="relative sm:col-span-2">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-mint-dark" /> Postcode</label>
              <input data-testid="review-postcode" value={addr.postcode} placeholder="Start typing e.g. SW1A"
                onChange={(e) => onPostcode(e.target.value)} autoComplete="off"
                className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
              {pcSuggestions.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-auto" data-testid="postcode-suggestions">
                  {pcSuggestions.map((pc) => (
                    <button key={pc} type="button" data-testid={`postcode-option-${pc.replace(/\s/g, "")}`} onClick={() => selectPostcode(pc)}
                      className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm font-mono">{pc}</button>
                  ))}
                </div>
              )}
              {pcLoading && <span className="absolute right-3 top-9 text-xs text-slate-400">…</span>}
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-slate-700">Flat / door number / property name <span className="text-slate-400 font-normal">(recommended)</span></label>
              <input data-testid="review-unit" value={unit} placeholder="e.g. Flat 2, or 'Rose Cottage'"
                onChange={(e) => setUnit(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
              <p className="mt-1 text-xs text-slate-400">Helps others find the exact property, especially in shared buildings.</p>
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-slate-700">Street address / building</label>
              <input data-testid="review-address_line" value={addr.address_line} placeholder="18 Marine Parade"
                onChange={(e) => setAddr({ ...addr, address_line: e.target.value })}
                className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-slate-700">City / Town</label>
              <input data-testid="review-city" value={addr.city} placeholder="Auto-filled from postcode"
                onChange={(e) => setAddr({ ...addr, city: e.target.value })}
                className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
              <p className="mt-1 text-xs text-slate-400">We fill this from your postcode — please correct it if the town looks wrong.</p>
            </div>
          </div>
          )}

          {/* STEP 2 — Ratings, dates, responsible, title, body */}
          {step === 2 && (
          <div className="flex flex-col gap-8" data-testid="review-step-2">
            <div className="grid sm:grid-cols-2 gap-6">
              {RATING_KEYS.map((k) => (
                <div key={k}>
                  <label className="text-sm font-semibold text-navy">{RATING_LABELS[k]}</label>
                  <div className="mt-2"><StarInput testid={`rating-${k}`} value={ratings[k]} onChange={(v) => setRatings({ ...ratings, [k]: v })} /></div>
                </div>
              ))}
              <p className="sm:col-span-2 text-xs text-slate-400 font-mono">1 = Very bad · 5 = No problems</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 border-t border-slate-100 pt-6">
              <div>
                <label className="text-sm font-medium text-slate-700">Tenancy start <span className="text-slate-400 font-normal">(month &amp; year)</span></label>
                <input data-testid="review-start" type="month" value={start} max={new Date().toISOString().slice(0, 7)} onChange={(e) => setStart(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Tenancy end <span className="text-slate-400 font-normal">(or leave blank if current)</span></label>
                <input data-testid="review-end" type="month" value={end} min={start || undefined} max={new Date().toISOString().slice(0, 7)} onChange={(e) => setEnd(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
              </div>
            </div>
            <div className="border-t border-slate-100 pt-6">
              <label className="text-sm font-semibold text-navy">Who was responsible for the property?</label>
              <div className="mt-2 grid grid-cols-2 gap-3">
                {[{ v: "landlord", t: "Private landlord" }, { v: "letting_agent", t: "Letting agent" }].map((o) => (
                  <button key={o.v} type="button" data-testid={`responsible-${o.v}`} onClick={() => setResponsible(o.v)}
                    className={`p-3 rounded-xl border-2 text-sm font-semibold transition-colors ${responsible === o.v ? "border-mint bg-mint-soft text-navy" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                    {o.t}
                  </button>
                ))}
              </div>
              <div className="mt-3">
                <input data-testid="responsible-email" type="email" value={respEmail} onChange={(e) => setRespEmail(e.target.value)}
                  placeholder={`Email of the ${responsible === "letting_agent" ? "letting agent" : "landlord"} to notify (optional)`}
                  className="w-full border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
                <p className="mt-1.5 text-xs text-slate-400">If provided, we'll let them know a review has been left so they can respond — your email stays private.</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Review title</label>
              <input data-testid="review-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Great flat, slow repairs"
                className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Your experience</label>
              <textarea data-testid="review-body" value={body} onChange={(e) => setBody(e.target.value)} rows={5}
                placeholder="Describe your time living here…"
                className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
            </div>
          </div>
          )}

          {/* STEP 3 — Photo, agreement, submit */}
          {step === 3 && (
          <div className="flex flex-col gap-8" data-testid="review-step-3">
            <div>
              <label className="text-sm font-semibold text-navy">Photo of the property <span className="text-slate-400 font-normal">(optional)</span></label>
              <label htmlFor="property-photo" data-testid="review-photo-zone"
                className="mt-2 flex items-center gap-4 border-2 border-dashed border-slate-300 rounded-xl p-4 cursor-pointer hover:border-mint transition-colors">
                {photoPreview ? (
                  <img src={photoPreview} alt="preview" className="w-20 h-20 object-cover rounded-lg" />
                ) : (
                  <div className="w-20 h-20 bg-slate-50 rounded-lg flex items-center justify-center"><Camera className="w-7 h-7 text-slate-400" /></div>
                )}
                <span className="text-sm text-slate-600">{photo ? photo.name : "Add a photo of the property so others recognise it"}</span>
                <input id="property-photo" data-testid="review-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => onPhoto(e.target.files[0])} />
              </label>
            </div>

            <div>
              <label className="text-sm font-semibold text-navy">Tenancy agreement (photo or PDF)</label>
              <label htmlFor="agreement-file" data-testid="review-upload-zone"
                className={`mt-2 flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${file ? "border-mint bg-mint-soft/40" : "border-slate-300 hover:border-mint"}`}>
                {file ? (
                  <div className="flex flex-col items-center gap-2 text-center" data-testid="agreement-uploaded">
                    {filePreview ? (
                      <img src={filePreview} alt="agreement preview" className="w-24 h-24 object-cover rounded-lg border border-mint/40" />
                    ) : (
                      <div className="w-24 h-24 bg-white rounded-lg border border-mint/40 flex items-center justify-center"><UploadCloud className="w-8 h-8 text-mint-dark" /></div>
                    )}
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CheckCircle2 className="w-4 h-4" /> Uploaded: {file.name}</span>
                    <span className="text-xs text-slate-500 underline">Click to change</span>
                  </div>
                ) : (
                  <>
                    <UploadCloud className="w-8 h-8 text-slate-400" />
                    <span className="text-sm text-slate-600">Click to upload your agreement</span>
                    <span className="text-xs text-slate-400 text-center max-w-sm">We only need the <b>first page</b> showing your name, the property address and the date — a screenshot or phone photo is fine. Check your deposit-scheme emails or junk folder, or ask your landlord/agent for a copy.</span>
                  </>
                )}
                <input id="agreement-file" data-testid="review-file-input" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => onFile(e.target.files[0])} />
              </label>
              <p className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-mint-dark" /> GDPR: your agreement is deleted the moment our team verifies your review.
              </p>
            </div>

            <div className="bg-mint-soft/60 border border-mint/30 rounded-xl p-4">
              <p className="text-xs text-slate-600 leading-relaxed">Please keep your review honest, accurate and based on your own first-hand experience. Reviews must follow our <a href="/review-guidelines" target="_blank" rel="noreferrer" className="text-mint-dark font-semibold underline">Review Guidelines</a> — false, malicious or defamatory content may be removed and could have legal consequences.</p>
              <label className="mt-3 flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input data-testid="review-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#10B981]" />
                <span>I confirm this review is truthful and I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-mint-dark font-semibold underline">Terms &amp; Conditions</a>.</span>
              </label>
            </div>
          </div>
          )}

          {error && <p className="text-sm text-destructive" data-testid="review-error">{error}</p>}

          {/* Navigation */}
          <div className="flex items-center justify-between gap-3">
            {step > 1
              ? <button type="button" data-testid="review-back" onClick={goBack} className="border border-slate-300 text-slate-700 font-semibold px-5 py-3 rounded-xl hover:bg-slate-50 transition-colors">Back</button>
              : <span />}
            {step < 3
              ? <button type="button" data-testid="review-next" onClick={goNext} className="bg-navy text-white font-semibold px-6 py-3 rounded-xl hover:bg-navy-600 transition-colors">Continue →</button>
              : <button type="submit" data-testid="submit-review-btn" disabled={submitting} className="bg-mint text-white font-semibold px-6 py-3 rounded-xl hover:bg-mint-dark transition-colors disabled:opacity-60">{submitting ? "Submitting…" : "Submit for verification"}</button>}
          </div>
        </form>
      </div>
    </div>
  );
}
